import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { auth } from '@obis/firebase-config';
import { XMarkIcon, CameraIcon, DocumentArrowUpIcon, SparklesIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { extractAdmissionFormData, ExtractedAdmissionData } from '../services/geminiOcrService';
import { ageFromDateOfBirth } from '../utils/ageFromDateOfBirth';
import { uploadAdmissionForm, uploadLeadDocument, validateFormImage, createObjectURL, revokeObjectURL } from '../services/formStorageService';

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLeadAdded: () => void;
  branchId: string;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({ isOpen, onClose, onLeadAdded, branchId }) => {
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    studentName: '',
    studentAge: '',
    studentGender: '',
    currentSchool: '',
    currentGrade: '',
    preferredGrade: '',
    inquirySource: 'walk-in',
    inquirySourceOther: '',
    priority: 'warm',
    notes: '',
    team_id: '',
    referral_type: '',
    assignedTo: '',
    // Extended fields from admission form
    dateOfBirth: '',
    fatherName: '',
    motherName: '',
    religion: '',
    alternatePhone: '',
    address: '',
    locality: '',
    pinCode: '',
    formImageUrl: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // AI Scan state
  const [scanMode, setScanMode] = useState<'manual' | 'scan'>('manual');
  const [formImage, setFormImage] = useState<File | null>(null);
  const [bcImage, setBcImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [bcPreviewUrl, setBcPreviewUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [extracted, setExtracted] = useState<ExtractedAdmissionData | null>(null);
  const [aiFields, setAiFields] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bcInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) fetchUsers();
  }, [isOpen]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => { 
      if (imagePreviewUrl) revokeObjectURL(imagePreviewUrl); 
      if (bcPreviewUrl) revokeObjectURL(bcPreviewUrl);
    };
  }, [imagePreviewUrl, bcPreviewUrl]);

  useEffect(() => {
    if (!formData.dateOfBirth) return;
    const age = ageFromDateOfBirth(formData.dateOfBirth);
    if (!age || age === formData.studentAge) return;
    setFormData(prev => ({ ...prev, studentAge: age }));
  }, [formData.dateOfBirth, formData.studentAge]);

  // Helper: Title Case
  const toTitleCase = (str: string) => {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const fetchUsers = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data.filter((u: any) =>
          u.isActive && ['admissions_counselor', 'admissions_manager', 'admin', 'superadmin'].includes(u.role)
        ));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // ── Duplicate detection ──────────────────────────────────────────────────
  const checkForDuplicates = useCallback(async () => {
    if ((!formData.parentEmail && !formData.parentPhone) || !formData.studentName) {
      setWarning(''); return;
    }
    setCheckingDuplicate(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken(true);
      const response = await fetch(
        `${API_BASE_URL}/leads?search=${encodeURIComponent(formData.studentName)}&branchId=${branchId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success && data.data?.length > 0) {
        const matches = data.data.filter((lead: any) =>
          (formData.parentEmail && lead.parentEmail?.toLowerCase() === formData.parentEmail.toLowerCase()) ||
          (formData.parentPhone && lead.parentPhone === formData.parentPhone)
        );
        if (matches.length > 0) {
          const m = matches[0];
          setWarning(`⚠️ Similar lead found: ${m.studentName} (${m.stage}) - Email: ${m.parentEmail || 'N/A'}, Phone: ${m.parentPhone || 'N/A'}`);
        } else setWarning('');
      } else setWarning('');
    } catch { setWarning(''); }
    finally { setCheckingDuplicate(false); }
  }, [formData.parentEmail, formData.parentPhone, formData.studentName, branchId]);

  useEffect(() => {
    const t = setTimeout(checkForDuplicates, 500);
    return () => clearTimeout(t);
  }, [checkForDuplicates]);

  // ── Image upload & AI scan ───────────────────────────────────────────────
  const handleImageSelected = async (file: File, type: 'form' | 'bc') => {
    const validationError = validateFormImage(file);
    if (validationError) { setScanError(validationError); return; }

    setScanError('');
    if (type === 'form') {
      setFormImage(file);
      if (imagePreviewUrl) revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(createObjectURL(file));
      // Re-scan with both if available
      await scanWithGemini(file, bcImage || undefined);
    } else {
      setBcImage(file);
      if (bcPreviewUrl) revokeObjectURL(bcPreviewUrl);
      setBcPreviewUrl(createObjectURL(file));
      // Re-scan with both if available
      await scanWithGemini(formImage || undefined, file);
    }
  };

  const scanWithGemini = async (file?: File, certificate?: File) => {
    if (!file && !certificate) return;
    setScanning(true);
    setScanError('');
    setExtracted(null);
    try {
      const data = await extractAdmissionFormData(file || certificate!, certificate);
      setExtracted(data);

      // Figure out which fields were populated by AI
      const populated = new Set<string>();
      const updates: Partial<typeof formData> = {};

      // Name Formatting Logic (Source of Truth check handled by Service but reinforced here)
      const finalStudentName = toTitleCase(data.studentName || '');
      const finalFatherName = toTitleCase(data.fatherName || '');
      const finalMotherName = toTitleCase(data.motherName || '');

      if (finalStudentName) { updates.studentName = finalStudentName; populated.add('studentName'); }
      if (data.gender)          { updates.studentGender = data.gender;             populated.add('studentGender'); }
      if (data.dateOfBirth)     { updates.dateOfBirth = data.dateOfBirth;          populated.add('dateOfBirth'); }
      if (data.religion)        { updates.religion = data.religion;                populated.add('religion'); }
      if (data.classApplyingFor){ updates.preferredGrade = data.classApplyingFor;  populated.add('preferredGrade'); }
      if (data.previousSchool)  { updates.currentSchool = data.previousSchool;     populated.add('currentSchool'); }
      
      if (finalFatherName)      { 
        updates.fatherName = finalFatherName; populated.add('fatherName');
        if (!formData.parentName) { updates.parentName = finalFatherName; populated.add('parentName'); } 
      }
      if (finalMotherName)      { updates.motherName = finalMotherName;            populated.add('motherName'); }
      
      if (data.primaryPhone)    { updates.parentPhone = data.primaryPhone;         populated.add('parentPhone'); }
      if (data.alternatePhone)  { updates.alternatePhone = data.alternatePhone;    populated.add('alternatePhone'); }
      if (data.email)           { updates.parentEmail = data.email.toLowerCase();  populated.add('parentEmail'); }
      if (data.occupation)      { updates.notes = `Occupation: ${data.occupation}`; populated.add('notes'); }
      if (data.address)         { updates.address = data.address;                  populated.add('address'); }
      if (data.locality)        { updates.locality = data.locality;                populated.add('locality'); }
      if (data.pinCode)         { updates.pinCode = data.pinCode;                  populated.add('pinCode'); }

      // Auto-set inquiry source to walk-in for physical forms
      updates.inquirySource = 'walk-in';

      setAiFields(populated);
      setFormData(prev => ({ ...prev, ...updates }));
    } catch (err: any) {
      setScanError(`Scan failed: ${err.message}. You can still fill the form manually.`);
    } finally {
      setScanning(false);
    }
  };

  const handleDrop = (e: React.DragEvent, type: 'form' | 'bc') => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleImageSelected(file, type);
  };

  // ── Form submit ──────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const finalInquirySource = formData.inquirySource === 'other' && formData.inquirySourceOther
        ? formData.inquirySourceOther : formData.inquirySource;

      // Staff-created leads must be stamped with the caller's tenant. Use the
      // authenticated endpoint (server resolves tenantId from the ID token) when
      // signed in; only fall back to the public endpoint for anonymous capture.
      const currentUser = auth.currentUser;
      const idToken = currentUser ? await currentUser.getIdToken() : null;
      const createUrl = idToken ? `${API_BASE_URL}/leads` : `${API_BASE_URL}/public/leads`;
      const createHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (idToken) createHeaders['Authorization'] = `Bearer ${idToken}`;

      // Create lead first to get the ID
      const response = await fetch(createUrl, {
        method: 'POST',
        headers: createHeaders,
        body: JSON.stringify({
          ...formData,
          studentName: toTitleCase(formData.studentName),
          parentName: toTitleCase(formData.parentName),
          inquirySource: finalInquirySource,
          studentAge: parseInt(formData.studentAge) || 0,
          branchId,
          hasPhysicalForm: !!formImage || !!bcImage,
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create lead');

      // Upload images if present, then update lead with URLs
      if ((formImage || bcImage) && data.data?.id) {
        try {
          const docUrls: { url: string; type: string }[] = [];
          if (formImage) {
            const url = await uploadLeadDocument(formImage, branchId, data.data.id, 'admission_form');
            docUrls.push({ url, type: 'admission_form' });
          }
          if (bcImage) {
            const url = await uploadLeadDocument(bcImage, branchId, data.data.id, 'birth_certificate');
            docUrls.push({ url, type: 'birth_certificate' });
          }

          // Update lead with the URLs
          const user = auth.currentUser;
          if (user) {
            const token = await user.getIdToken(true);
            await fetch(`${API_BASE_URL}/leads/${data.data.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ 
                formImageUrl: docUrls[0].url,
                documents: docUrls
              })
            });
          }
        } catch (uploadErr) {
          console.warn('Image upload failed, lead still created:', uploadErr);
        }
      }

      resetForm();
      onLeadAdded();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      parentName: '', parentEmail: '', parentPhone: '', studentName: '',
      studentAge: '', studentGender: '', currentSchool: '', currentGrade: '',
      preferredGrade: '', inquirySource: 'walk-in', inquirySourceOther: '',
      priority: 'warm', notes: '', team_id: '', referral_type: '', assignedTo: '',
      dateOfBirth: '', fatherName: '', motherName: '', religion: '',
      alternatePhone: '', address: '', locality: '', pinCode: '', formImageUrl: '',
    });
    setFormImage(null);
    setBcImage(null);
    setImagePreviewUrl('');
    setBcPreviewUrl('');
    setExtracted(null);
    setAiFields(new Set());
    setScanError('');
    setScanMode('manual');
  };

  // Helper: highlight AI-extracted fields
  const aiFieldClass = (name: string) =>
    aiFields.has(name) ? 'input-field border-l-4 border-l-blue-400 bg-blue-50' : 'input-field';

  const grades = ['Nursery', 'Jr.KG', 'Sr.KG', 'Class I', 'Class II', 'Class III', 'Class IV', 'Class V', 'Class VI', 'Class VII'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Add New Lead</h3>
            <p className="text-xs text-gray-500 mt-0.5">Enter manually or scan a physical admission form</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-5">
          <button
            type="button"
            onClick={() => setScanMode('manual')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border transition-all ${
              scanMode === 'manual'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            ✏️ Fill Manually
          </button>
          <button
            type="button"
            onClick={() => setScanMode('scan')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-2 ${
              scanMode === 'scan'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-blue-600 border-blue-300 hover:border-blue-400'
            }`}
          >
            <SparklesIcon className="h-4 w-4" />
            📸 Scan Admission Form (AI)
          </button>
        </div>

        {/* ── SCAN PANEL ── */}
        {scanMode === 'scan' && (
          <div className="mb-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Admission Form Upload */}
              <div
                onDrop={(e) => handleDrop(e, 'form')}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  formImage ? 'border-green-300 bg-green-50' : 'border-blue-300 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {formImage ? (
                  <div className="flex flex-col items-center">
                    <img src={imagePreviewUrl} alt="Form" className="w-16 h-20 object-cover rounded border mb-2 shadow-sm" />
                    <p className="text-[10px] font-bold text-green-700 uppercase">Admission Form Loaded</p>
                    <button type="button" className="text-[10px] text-gray-500 underline mt-1">Change</button>
                  </div>
                ) : (
                  <>
                    <CameraIcon className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                    <p className="text-xs font-bold text-gray-700">Upload Admission Form</p>
                    <p className="text-[10px] text-gray-500 mt-1">Click or drag & drop</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageSelected(f, 'form'); }}
                />
              </div>

              {/* Birth Certificate Upload */}
              <div
                onDrop={(e) => handleDrop(e, 'bc')}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => bcInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  bcImage ? 'border-purple-300 bg-purple-50' : 'border-purple-200 hover:border-purple-400 hover:bg-purple-50'
                }`}
              >
                {bcImage ? (
                  <div className="flex flex-col items-center">
                    <img src={bcPreviewUrl} alt="BC" className="w-16 h-20 object-cover rounded border mb-2 shadow-sm" />
                    <p className="text-[10px] font-bold text-purple-700 uppercase">Birth Certificate Loaded</p>
                    <button type="button" className="text-[10px] text-gray-500 underline mt-1">Change</button>
                  </div>
                ) : (
                  <>
                    <DocumentArrowUpIcon className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-xs font-bold text-gray-700">Upload Birth Certificate</p>
                    <p className="text-[10px] text-gray-400 mt-1">Highly Recommended for Accuracy</p>
                  </>
                )}
                <input
                  ref={bcInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageSelected(f, 'bc'); }}
                />
              </div>
            </div>

            {/* Processing Status */}
            {(scanning || extracted || scanError) && (
              <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                {scanning && (
                  <div className="flex items-center gap-3 text-blue-600">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold">AI is cross-referencing documents...</p>
                      <p className="text-xs text-gray-500">Verifying name and DOB accuracy</p>
                    </div>
                  </div>
                )}

                {!scanning && extracted && !scanError && (
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        Extracted {aiFields.size} fields
                      </p>
                      <p className="text-xs text-green-700 font-medium">
                        {bcImage ? '✦ Birth Certificate verified as source of truth' : '✦ Extraction complete (BC highly recommended)'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {aiFields.has('studentName') && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Name Extracted</span>}
                        {aiFields.has('dateOfBirth') && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">DOB Extracted</span>}
                        {aiFields.has('parentPhone') && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Contact Extracted</span>}
                      </div>
                    </div>
                  </div>
                )}

                {!scanning && scanError && (
                  <div className="flex items-start gap-2 text-amber-700 bg-amber-50 rounded-lg p-3">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <p className="text-xs">{scanError}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
        )}
        {warning && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded flex items-start gap-2">
            <span className="text-lg">⚠️</span>
            <div>
              <p className="font-semibold text-sm">Possible Duplicate Detected</p>
              <p className="text-sm mt-1">{warning}</p>
              <p className="text-xs text-yellow-700 mt-2">Please verify before creating. You can still proceed if this is a different person.</p>
            </div>
          </div>
        )}

        {/* ── FORM ── */}
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Student Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              Student Information
              {aiFields.size > 0 && <span className="text-xs font-normal text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">✦ AI filled</span>}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student Name *</label>
                <input type="text" required value={formData.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                  className={aiFieldClass('studentName')} placeholder="e.g. Md Moazzam" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <input type="date" value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className={aiFieldClass('dateOfBirth')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select required value={formData.studentGender}
                  onChange={(e) => setFormData({ ...formData, studentGender: e.target.value })}
                  className={aiFieldClass('studentGender')}>
                  <option value="">Select Gender</option>
                  <option value="male">Boy</option>
                  <option value="female">Girl</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                <input type="text" value={formData.religion}
                  onChange={(e) => setFormData({ ...formData, religion: e.target.value })}
                  className={aiFieldClass('religion')} placeholder="e.g. Islam" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                <input type="number" min="0" max="25" value={formData.studentAge}
                  readOnly={Boolean(formData.dateOfBirth)}
                  onChange={(e) => setFormData({ ...formData, studentAge: e.target.value })}
                  className={`input-field ${formData.dateOfBirth ? 'bg-gray-50' : ''}`} />
                {formData.dateOfBirth && (
                  <p className="text-xs text-gray-500 mt-1">Calculated from date of birth</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Grade *</label>
                <select required value={formData.preferredGrade}
                  onChange={(e) => setFormData({ ...formData, preferredGrade: e.target.value })}
                  className={aiFieldClass('preferredGrade')}>
                  <option value="">Select Grade</option>
                  {grades.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Previous School</label>
                <input type="text" value={formData.currentSchool}
                  onChange={(e) => setFormData({ ...formData, currentSchool: e.target.value })}
                  className={aiFieldClass('currentSchool')} placeholder="Sacred Heart School" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Grade</label>
                <select value={formData.currentGrade}
                  onChange={(e) => setFormData({ ...formData, currentGrade: e.target.value })}
                  className="input-field">
                  <option value="">Select Grade</option>
                  {grades.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Parent / Guardian Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Parent / Guardian Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
                <input type="text" value={formData.fatherName}
                  onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                  className={aiFieldClass('fatherName')} placeholder="Md Hasibur" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Name</label>
                <input type="text" value={formData.motherName}
                  onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                  className={aiFieldClass('motherName')} placeholder="Aaliya Nasrin" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Name * <span className="text-xs text-gray-400">(for CRM record)</span></label>
                <input type="text" required value={formData.parentName}
                  onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                  className={aiFieldClass('parentName')} placeholder="Primary contact name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input type="email" value={formData.parentEmail}
                  onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                  className={aiFieldClass('parentEmail')} placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Mobile *</label>
                <input type="tel" required value={formData.parentPhone}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d+]/g, '');
                    if (!value.startsWith('+91')) {
                      if (value.startsWith('91')) value = '+' + value;
                      else if (!value.startsWith('+') && value.length > 0) value = '+91' + value;
                    }
                    if (value.length > 13) value = value.substring(0, 13);
                    setFormData({ ...formData, parentPhone: value });
                  }}
                  className={aiFieldClass('parentPhone')} placeholder="+91XXXXXXXXXX" />
                <p className="text-xs text-gray-500 mt-1">Format: +91XXXXXXXXXX</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Mobile</label>
                <input type="tel" value={formData.alternatePhone}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d+]/g, '');
                    if (!value.startsWith('+91') && value.length > 0 && !value.startsWith('+')) value = '+91' + value;
                    if (value.length > 13) value = value.substring(0, 13);
                    setFormData({ ...formData, alternatePhone: value });
                  }}
                  className={aiFieldClass('alternatePhone')} placeholder="+91XXXXXXXXXX" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Address Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address</label>
                <input type="text" value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={aiFieldClass('address')} placeholder="Flat/House, Building, Street" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area / Locality</label>
                <input type="text" value={formData.locality}
                  onChange={(e) => setFormData({ ...formData, locality: e.target.value })}
                  className={aiFieldClass('locality')} placeholder="Gulshan Colony" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                <input type="text" maxLength={6} value={formData.pinCode}
                  onChange={(e) => setFormData({ ...formData, pinCode: e.target.value.replace(/\D/g, '') })}
                  className={aiFieldClass('pinCode')} placeholder="700105" />
              </div>
            </div>
          </div>

          {/* Lead Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Lead Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Source *</label>
                <select required value={formData.inquirySource}
                  onChange={(e) => setFormData({ ...formData, inquirySource: e.target.value, inquirySourceOther: '' })}
                  className="input-field">
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="walk-in">Walk-in</option>
                  <option value="social_media">Social Media</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="event">Event</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {formData.inquirySource === 'other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Please Specify *</label>
                  <input type="text" required value={formData.inquirySourceOther}
                    onChange={(e) => setFormData({ ...formData, inquirySourceOther: e.target.value })}
                    className="input-field" placeholder="Specify inquiry source" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
                <select required value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="input-field">
                  <option value="hot">Hot</option>
                  <option value="warm">Warm</option>
                  <option value="cold">Cold</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  className="input-field">
                  <option value="">Assign to me</option>
                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>{u.name} ({u.role.replace('_', ' ')})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Leave empty to assign to yourself</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                <select value={formData.team_id}
                  onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                  className="input-field">
                  <option value="">Select Team</option>
                  <option value="blue">Blue Team</option>
                  <option value="green">Green Team</option>
                  <option value="red">Red Team</option>
                  <option value="yellow">Yellow Team</option>
                  <option value="orange">Orange Team</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referral Type</label>
                <select value={formData.referral_type}
                  onChange={(e) => setFormData({ ...formData, referral_type: e.target.value })}
                  className="input-field">
                  <option value="">No Referral</option>
                  <option value="parent">Parent Referral</option>
                  <option value="student">Student Referral</option>
                  <option value="staff">Staff Referral</option>
                  <option value="community">Community Referral</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Referral leads earn 20 points (vs 5 for regular)</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className={aiFieldClass('notes')} rows={2}
                  placeholder="Any additional information..." />
              </div>
            </div>
          </div>

          {/* Form image badge */}
          {formImage && (
            <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <DocumentArrowUpIcon className="h-4 w-4 flex-shrink-0" />
              <span>📎 Original admission form will be attached to this lead after creation</span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="btn-outline" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading || scanning}>
              {loading ? 'Creating...' : scanning ? 'Scanning...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLeadModal;
